import { ProfileRole } from "@/api/model";

/**
 * User role constants and metadata.
 * Role values come from the Orval-generated ProfileRole enum.
 */

// Re-export the generated ProfileRole constant for convenience
export { ProfileRole };

// Type for role values
export type RoleValue = (typeof ProfileRole)[keyof typeof ProfileRole];

/**
 * Display labels for roles
 */
export const ROLE_LABELS: Record<RoleValue, string> = {
  [ProfileRole.player]: "Player",
  [ProfileRole.coach]: "Staff",
  // The backend role value stays "contact"; the user-facing label is Parent
  // per the Users Refresh design.
  [ProfileRole.contact]: "Parent",
  [ProfileRole.admin]: "Admin",
};

/**
 * Descriptions for roles (used in UI tooltips and help text)
 */
export const ROLE_DESCRIPTIONS: Record<RoleValue, string> = {
  [ProfileRole.player]: "Athlete in your organization",
  [ProfileRole.coach]: "Staff member in your organization",
  [ProfileRole.contact]: "Parent or guardian of a player",
  [ProfileRole.admin]: "Administrator with full access",
};

/**
 * The four choices on step 1 of the Create User drawer, and the copy each one
 * carries through to step 2 (Users Refresh design). One role per create — extra
 * roles are added later from the user's detail page.
 *
 * Kept separate from ROLE_LABELS/ROLE_DESCRIPTIONS above: those serve the list
 * badges and filters, where the wording is deliberately different.
 */
export type CreateRoleKey = "player" | "contact" | "staff" | "none";

export interface CreateRoleOption {
  key: CreateRoleKey;
  icon: string;
  label: string;
  description: string;
  title: string;
  subtitle: string;
  cta: string;
}

export const CREATE_ROLE_OPTIONS: CreateRoleOption[] = [
  {
    key: "player",
    icon: "pi pi-user",
    label: "Player",
    description: "Athlete in your organization",
    title: "Create player",
    subtitle: "Athlete in your organization.",
    cta: "Create player",
  },
  {
    key: "contact",
    icon: "pi pi-users",
    label: "Parent",
    description: "Can view, manage, and pay for players",
    title: "Create parent",
    subtitle: "Parent or guardian. Can view and manage players.",
    cta: "Create parent",
  },
  {
    key: "staff",
    icon: "pi pi-briefcase",
    label: "Staff",
    description: "Staff member",
    title: "Create staff member",
    subtitle: "Staff member",
    cta: "Create staff member",
  },
  {
    key: "none",
    icon: "pi pi-ban",
    label: "Continue without role",
    description: "Just create the person. You can add a role later.",
    title: "Create user",
    subtitle: "Just create the person. You can add a role later.",
    cta: "Create",
  },
];

/**
 * Role validation rules
 */
export const ROLE_RULES = {
  /**
   * Pairs of roles that cannot be assigned to the same user
   */
  MUTUALLY_EXCLUSIVE_PAIRS: [[ProfileRole.player, ProfileRole.coach]] as const,
};

/**
 * Filter options for UI dropdowns (used in user list filters)
 */
export const ROLE_FILTER_OPTIONS = [
  { label: ROLE_LABELS[ProfileRole.coach], value: ProfileRole.coach },
  { label: ROLE_LABELS[ProfileRole.player], value: ProfileRole.player },
  { label: ROLE_LABELS[ProfileRole.contact], value: ProfileRole.contact },
  { label: ROLE_LABELS[ProfileRole.admin], value: ProfileRole.admin },
];

/**
 * Helper to check if two roles are mutually exclusive
 */
export function areMutuallyExclusive(
  role1: RoleValue,
  role2: RoleValue
): boolean {
  return ROLE_RULES.MUTUALLY_EXCLUSIVE_PAIRS.some(
    ([a, b]) => (a === role1 && b === role2) || (a === role2 && b === role1)
  );
}

/**
 * Helper to get role display label
 */
export function getRoleLabel(role: RoleValue): string {
  return ROLE_LABELS[role];
}

/**
 * Helper to get role description
 */
export function getRoleDescription(role: RoleValue): string {
  return ROLE_DESCRIPTIONS[role];
}
